import { cloneDeep } from 'lodash'
import { isNotEmptyElement } from '@feedyou/utils'

import { NlpModelSheets } from './types'
import {
  BotFlow,
  IntentHeadline,
  NlpExample,
  NlpIntent,
  NlpModel,
  NlpTrigger,
  NlpVariable
} from '@feedyou/schema'
import {
  HeaderModelExampleKeys,
  HeaderModelIntentKeys,
  HeaderModelTriggerKeys,
  HeaderModelVariableKeys
} from './constants'

export function convertXLSToNlpModels(
  tree: BotFlow,
  nlpModelSheets: NlpModelSheets[]
): BotFlow | Error {
  try {
    const bot = cloneDeep(tree)
    return {
      ...bot,
      nlp: {
        ...bot.nlp,
        models: fillNlpModelsFromSheets(bot?.nlp?.models || [], nlpModelSheets)
      }
    }
  } catch (error) {
    return error as Error
  }
}

function fillNlpModelsFromSheets(
  nlpModels: NlpModel[],
  nlpModelSheets: NlpModelSheets[]
): NlpModel[] {
  const nlpModelsCopy = cloneDeep(nlpModels)
  nlpModelSheets.forEach(nlpModelSheet => {
    const index = nlpModels?.findIndex(model => nlpModelSheet.modelId === model.id)
    if (index === undefined || index < 0) {
      nlpModelsCopy.push({ id: nlpModelSheet.modelId, intents: fillNlpModelIntents(nlpModelSheet) })
      return
    }

    nlpModelsCopy[index].intents = fillNlpModelIntents(nlpModelSheet, nlpModels[index])
  })
  return nlpModelsCopy
}

function fillNlpModelIntents(nlpModelSheet: NlpModelSheets, nlpModel?: NlpModel): NlpIntent[] {
  const intents: NlpIntent[] = []
  const modelId = nlpModelSheet.modelId
  const intentsModel = nlpModel?.intents
  const intentsSheet = nlpModelSheet.intents?.filter(isNotEmptyElement)
  const { INTENT_ID, INTENT_NAME, INTENT_TYPE } = HeaderModelIntentKeys

  const header = intentsSheet?.shift() || []
  const indexIntentId = getColumnIndexOrThrowException(header, INTENT_ID)
  const indexIntentName = getColumnIndexOrThrowException(header, INTENT_NAME)
  const indexIntentType = getColumnIndex(header, INTENT_TYPE) //note: temp BC solution

  return intentsSheet.map(intentSheet => {
    const intentId = intentSheet[indexIntentId]
    const intentName = intentSheet[indexIntentName]
    const intentType = indexIntentType ? intentSheet[indexIntentType] : ''

    throwIfIntentIdAlreadyExistsInList(modelId, intentId, intents)

    const rules = getIntentRules(intentId, cloneDeep(nlpModelSheet))
    const variables = getIntentVariables(intentId, cloneDeep(nlpModelSheet))
    const utterances = getIntentUtterances(intentId, cloneDeep(nlpModelSheet))

    if (isIntentHeadline(intentId, intentType, intentsModel)) {
      return fillIntentHeadline(intentId, intentName, intentsModel)
    }
    return fillIntent(intentId, intentName, rules, variables, utterances, intentsModel)
  })
}

function isIntentHeadline(
  intentId: string,
  intentType: string,
  intentsModel: NlpIntent[] = []
): boolean {
  return (
    intentType === 'headline' ||
    intentsModel.find(intent => intent.id === intentId)?.type === 'headline'
  )
}

function getIntentRules(intentId: string, nlpModelSheet: NlpModelSheets): NlpTrigger[] {
  const triggersSheet = nlpModelSheet.triggers?.filter(isNotEmptyElement)
  const header = triggersSheet?.shift() || []
  const triggersSheetFiltered = getFilteredRowsByIntentId(header, intentId, triggersSheet)

  const { RULE_REGEX } = HeaderModelTriggerKeys
  const indexRuleRegex = getColumnIndexOrThrowException(header, RULE_REGEX)

  return triggersSheetFiltered.map(triggerSheet => ({
    regex: triggerSheet[indexRuleRegex],
    type: 'trigger'
  }))
}

function getIntentVariables(intentId: string, nlpModelSheet: NlpModelSheets): NlpVariable[] {
  const variablesSheet = nlpModelSheet.variables?.filter(isNotEmptyElement)
  const header = variablesSheet?.shift() || []
  const variablesSheetFiltered = getFilteredRowsByIntentId(header, intentId, variablesSheet)

  const { VARIABLE_NAME, VARIABLE_VALUE } = HeaderModelVariableKeys
  const indexVariableName = getColumnIndexOrThrowException(header, VARIABLE_NAME)
  const indexVariableValue = getColumnIndexOrThrowException(header, VARIABLE_VALUE)

  return variablesSheetFiltered.map(variableSheet => ({
    name: variableSheet[indexVariableName],
    value: variableSheet[indexVariableValue]
  }))
}

function getIntentUtterances(intentId: string, nlpModelSheet: NlpModelSheets): NlpExample[] {
  const examplesSheet = nlpModelSheet.examples?.filter(isNotEmptyElement)
  const header = examplesSheet?.shift() || []
  const examplesSheetFiltered = getFilteredRowsByIntentId(header, intentId, examplesSheet)

  const { EXAMPLE_TEXT } = HeaderModelExampleKeys
  const indexExampleText = getColumnIndexOrThrowException(header, EXAMPLE_TEXT)

  return examplesSheetFiltered.map(exampleSheet => exampleSheet[indexExampleText])
}

function getFilteredRowsByIntentId(
  header: string[],
  intentId: string,
  rows: string[][]
): string[][] {
  const rowsCopy = cloneDeep(rows)
  const { INTENT_ID } = HeaderModelIntentKeys
  const index = getColumnIndexOrThrowException(header, INTENT_ID)

  return rowsCopy.filter(row => row[index] === intentId)
}

function fillIntent(
  intentId: string,
  intentName: string,
  rules: NlpTrigger[],
  variables: NlpVariable[],
  utterances: NlpExample[],
  intentsModel: NlpIntent[] = []
): NlpIntent {
  const intentFound = intentsModel.find(intent => intent.id === intentId)
  if (!intentFound) {
    return { id: intentId, name: intentName, rules, variables, utterances }
  }

  return {
    ...intentFound,
    name: intentName,
    rules: rules.length ? rules : undefined,
    variables: variables.length ? variables : undefined,
    utterances: utterances.length ? utterances : undefined
  }
}

function fillIntentHeadline(
  intentId: string,
  intentName: string,
  intentsModel: NlpIntent[] = []
): IntentHeadline {
  const intentFound = intentsModel.find(intent => intent.id === intentId) as IntentHeadline
  if (intentFound) {
    return { ...intentFound, text: intentName }
  }

  return { id: intentId, type: 'headline', text: intentName }
}

function throwIfIntentIdAlreadyExistsInList(
  modelId: string,
  intentId: string,
  intents: NlpIntent[]
) {
  if (intents.some(intent => intent.id === intentId)) {
    throw new Error(`Found duplicate intent ID ${intentId} for NLP model with ID ${modelId}`)
  }
}

type HeaderKey = string

function getColumnIndexOrThrowException(header: string[], targetColumnKey: HeaderKey): number {
  const index = getColumnIndex(header, targetColumnKey)
  if (typeof index === 'number') {
    return index
  }

  throw new Error(`Expected column name "${targetColumnKey}" were not found.`)
}

function getColumnIndex(header: string[], targetColumnKey: HeaderKey): number | undefined {
  const index = header?.findIndex(cell => cell?.trim() === targetColumnKey)
  if (index >= 0) {
    return index
  }
}
